<script lang="ts">
  import { onMount } from 'svelte';
  import { link } from 'svelte-spa-router';
  import { api } from '../../framework/js/api.js';
  import { toast } from '../../framework/js/toast.js';
  import { AppLoadingState } from '../../framework/components/app';
  import {
    agentSubjectKey,
    getAgentSubjectIcon,
    getAgentSubjectLabel,
  } from './adminListAgents.js';

  export let teamUuid: string | null = null;
  export let compact = false;

  interface CreditUsageRow {
    uuid: string;
    credits_used: number;
    subject_type: string;
    subject_uuid: string;
    subject_name?: string | null;
    subject_path?: string;
    team_uuid?: string | null;
    team_name?: string | null;
    user_uuid: string;
    user: { uuid: string; first_name: string; last_name: string; email: string } | null;
    prompt_tokens: number | null;
    completion_tokens: number | null;
    total_tokens: number | null;
    ai_model: string | null;
    estimated_cost: number | null;
    finished_at: string | null;
    created_at: string;
  }

  interface UsageSummary {
    credits_used: number;
    total_tokens: number;
    estimated_cost: number;
  }

  function getSubjectLabel(subjectType: string): string {
    return getAgentSubjectLabel(subjectType);
  }

  const SUBJECT_OPTIONS = [
    { value: '', label: 'All agents' },
    { value: 'brief', label: 'Built-to-Brief' },
    { value: 'assessment', label: 'Illuminator' },
    { value: 'gapfinder', label: 'GapFinder' },
  ];

  const moneyFmt = new Intl.NumberFormat(navigator.language, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 4,
    maximumFractionDigits: 4,
  });

  let items: CreditUsageRow[] = [];
  let summary: UsageSummary | null = null;
  let isLoading = false;
  let subjectTypeFilter = '';
  let fromDate = '';
  let toDate = '';
  let currentPage = 1;
  let paging = { current_page: 0, last_page: 1, per_page: 20, total: 0, from: 0, to: 0 };

  onMount(() => {
    loadUsage();
  });

  async function loadUsage() {
    if (compact && !teamUuid) {
      return;
    }
    isLoading = true;
    try {
      const params: Record<string, string | number> = {
        page: currentPage,
        per_page: compact ? 10 : 20,
      };
      if (subjectTypeFilter) params.subject_type = subjectTypeFilter;
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;

      const response: any = teamUuid
        ? await api.getAdminTeamCreditUsage(teamUuid, params)
        : await api.getAdminCreditUsage(params);

      if (response.success) {
        items = response.items || [];
        summary = response.summary || null;
        paging = response.paging || paging;
      } else {
        toast.showNotification(false, response.message || 'Failed to load credit usage');
        items = [];
        summary = null;
      }
    } catch (error) {
      toast.showNotification(false, 'Error loading credit usage');
      console.error(error);
      items = [];
      summary = null;
    } finally {
      isLoading = false;
    }
  }

  function applyFilters() {
    currentPage = 1;
    loadUsage();
  }

  function clearFilters() {
    subjectTypeFilter = '';
    fromDate = '';
    toDate = '';
    currentPage = 1;
    loadUsage();
  }

  function goToPage(page: number) {
    currentPage = page;
    loadUsage();
  }

  function formatDate(dateString: string | null): string {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleString(navigator.language, {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  }

  function formatTokens(value: number | null): string {
    if (value == null) return '—';
    return value.toLocaleString(navigator.language);
  }

  function formatCost(value: number | null): string {
    if (value == null) return '—';
    return moneyFmt.format(value);
  }

  function getItemDisplayName(row: CreditUsageRow): string {
    const n = row.subject_name?.trim();
    if (n) return n;
    return `Open ${getSubjectLabel(row.subject_type)}`;
  }

  function getAdminItemPath(row: CreditUsageRow): string {
    if (row.subject_type === 'brief') {
      return `/admin/briefs/${row.subject_uuid}`;
    }
    return row.subject_path || '';
  }

  $: hasFilters = subjectTypeFilter || fromDate || toDate;
</script>

<div class="credit-usage-list" class:compact class:admin-list={compact} class:admin-list--nested={compact}>
  {#if !compact}
    <div class="summary-row">
      <div class="summary-card">
        <span class="summary-label">Credits used</span>
        <span class="summary-value">{summary?.credits_used?.toLocaleString(navigator.language) ?? '—'}</span>
      </div>
      <div class="summary-card">
        <span class="summary-label">Total tokens</span>
        <span class="summary-value num">{summary?.total_tokens?.toLocaleString(navigator.language) ?? '—'}</span>
      </div>
      <div class="summary-card">
        <span class="summary-label">Est. AI cost</span>
        <span class="summary-value num">{summary ? formatCost(summary.estimated_cost) : '—'}</span>
      </div>
    </div>
  {/if}

  <div class="filters-bar filters-bar--labeled">
    <div class="filter-group">
      <label for="cu-from-{teamUuid || 'all'}" class="filter-label">From</label>
      <input
        id="cu-from-{teamUuid || 'all'}"
        type="date"
        bind:value={fromDate}
        on:change={applyFilters}
        class="filter-input"
      />
    </div>
    <div class="filter-group">
      <label for="cu-to-{teamUuid || 'all'}" class="filter-label">To</label>
      <input
        id="cu-to-{teamUuid || 'all'}"
        type="date"
        bind:value={toDate}
        on:change={applyFilters}
        class="filter-input"
      />
    </div>
    <div class="filter-group">
      <label for="cu-type-{teamUuid || 'all'}" class="filter-label">Agent</label>
      <select
        id="cu-type-{teamUuid || 'all'}"
        bind:value={subjectTypeFilter}
        on:change={applyFilters}
        class="filter-select"
      >
        {#each SUBJECT_OPTIONS as option}
          <option value={option.value}>{option.label}</option>
        {/each}
      </select>
    </div>
    {#if hasFilters}
      <button type="button" class="clear-filters-btn" on:click={clearFilters}>Clear filters</button>
    {/if}
    <button type="button" class="refresh-btn" on:click={() => loadUsage()} disabled={isLoading} title="Refresh" aria-label="Refresh">
      <i class="refresh icon" aria-hidden="true"></i>
    </button>
    <span class="results-count">
      {paging.total} {paging.total === 1 ? 'record' : 'records'}
    </span>
  </div>

  {#if isLoading}
    <AppLoadingState label="Loading credit usage…" />
  {:else if items.length === 0}
    <div class="empty-state">
      <i class="coins icon empty-icon" aria-hidden="true"></i>
      <p>No credit usage found for the selected filters.</p>
      {#if hasFilters}
        <button type="button" class="clear-filters-btn" on:click={clearFilters}>Clear filters</button>
      {/if}
    </div>
  {:else}
    <div class="table-wrap">
      <table class="data-table">
        <thead>
          <tr>
            <th><i class="calendar plus icon" aria-hidden="true"></i> Date</th>
            {#if !teamUuid}
              <th><i class="users icon" aria-hidden="true"></i> Team</th>
            {/if}
            <th><i class="cog icon" aria-hidden="true"></i> Agent</th>
            <th><i class="file outline icon" aria-hidden="true"></i> Output</th>
            <th><i class="user icon" aria-hidden="true"></i> User</th>
            <th class="num-col"><i class="coins icon" aria-hidden="true"></i> Credits</th>
            <th class="num-col"><i class="database icon" aria-hidden="true"></i> Tokens</th>
            <th class="num-col"><i class="dollar sign icon" aria-hidden="true"></i> Est. cost</th>
            <th><i class="microchip icon" aria-hidden="true"></i> Model</th>
          </tr>
        </thead>
        <tbody>
          {#each items as row (row.uuid)}
            <tr>
              <td class="date-cell">{formatDate(row.finished_at)}</td>
              {#if !teamUuid}
                <td>
                  {#if row.team_uuid}
                    <a use:link href="/admin/teams/{row.team_uuid}" class="team-link">{row.team_name || 'Team'}</a>
                  {:else}
                    <span class="text-muted">—</span>
                  {/if}
                </td>
              {/if}
              <td>
                <div class="type-wrapper">
                  <div class="type-icon" data-subject={agentSubjectKey(row.subject_type)}>
                    <i class="{getAgentSubjectIcon(row.subject_type)} icon" aria-hidden="true"></i>
                  </div>
                  <span class="type-label">{getSubjectLabel(row.subject_type)}</span>
                </div>
              </td>
              <td class="item-cell">
                {#if getAdminItemPath(row)}
                  <a
                    use:link
                    href={getAdminItemPath(row)}
                    class="item-link"
                    title={getItemDisplayName(row)}
                  >
                    <span class="item-link-text">{getItemDisplayName(row)}</span>
                    <i class="chevron right icon item-link-icon" aria-hidden="true"></i>
                  </a>
                {:else}
                  <span class="text-muted">—</span>
                {/if}
              </td>
              <td class="user-cell">
                {#if row.user}
                  <div class="user-name">{row.user.first_name} {row.user.last_name}</div>
                  <div class="user-email">{row.user.email}</div>
                {:else}
                  <span class="text-muted">—</span>
                {/if}
              </td>
              <td class="num-col">{row.credits_used}</td>
              <td class="num-col">{formatTokens(row.total_tokens)}</td>
              <td class="num-col">{formatCost(row.estimated_cost)}</td>
              <td class="model-cell">
                {#if row.ai_model}
                  <code class="model-code">{row.ai_model}</code>
                {:else}
                  <span class="text-muted">—</span>
                {/if}
              </td>
            </tr>
          {/each}
        </tbody>
      </table>
    </div>

    {#if paging.last_page > 1}
      <div class="pagination">
        <span class="pagination-info">
          Showing {paging.from || 0}–{paging.to || 0} of {paging.total}
        </span>
        <div class="pagination-buttons">
          <button type="button" class="pagination-btn" disabled={currentPage <= 1} on:click={() => goToPage(currentPage - 1)} aria-label="Previous page">Previous</button>
          <span class="page-indicator">Page {currentPage} of {paging.last_page}</span>
          <button type="button" class="pagination-btn" disabled={currentPage >= paging.last_page} on:click={() => goToPage(currentPage + 1)} aria-label="Next page">Next</button>
        </div>
      </div>
    {/if}
  {/if}
</div>

<style>
  .credit-usage-list {
    display: flex;
    flex-direction: column;
    gap: var(--space-4);
  }

  .item-cell {
    max-width: 14rem;
  }

  .model-cell {
    max-width: 12rem;
  }

  .model-code {
    font-size: 0.75rem;
    background: var(--greyish);
    padding: 0.125rem 0.375rem;
    border-radius: var(--radius-input);
    word-break: break-all;
  }

  .compact .summary-row {
    display: none;
  }
</style>
